import { useEffect, useState } from "react";
import api, { formatApiErrorDetail, money } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, X, FileCheck, CalendarPlus } from "lucide-react";

const statusStyle = {
  draft: "bg-muted text-muted-foreground",
  sent: "bg-accent/15 text-accent",
  accepted: "bg-primary/10 text-primary",
  declined: "bg-destructive/10 text-destructive",
};
const today = () => new Date().toISOString().slice(0, 10);
const plus14 = () => { const d = new Date(); d.setDate(d.getDate() + 14); return d.toISOString().slice(0, 10); };
const empty = () => ({
  customer_id: "", customer_name: "", customer_email: "",
  items: [{ description: "", quantity: 1, unit_price: 0, unit_cost: 0 }],
  tax_rate: 0, status: "draft", valid_until: plus14(), deposit_required: false, deposit_amount: 0, notes: "",
});

export default function Estimates() {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty());
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/estimates").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/catalog").then((r) => setCatalog(r.data));
  }, []);

  const openNew = () => { setForm(empty()); setEditing(null); setOpen(true); };
  const openEdit = (e) => { setForm({ ...empty(), ...e, items: e.items.length ? e.items : empty().items }); setEditing(e.id); setOpen(true); };

  const onCustomer = (id) => {
    const c = customers.find((x) => x.id === id);
    setForm((f) => ({ ...f, customer_id: id, customer_name: c ? c.name : "", customer_email: c ? c.email : "" }));
  };
  const setItem = (i, k, v) => setForm({ ...form, items: form.items.map((it, idx) => idx === i ? { ...it, [k]: v } : it) });
  const addItem = () => setForm({ ...form, items: [...form.items, { description: "", quantity: 1, unit_price: 0, unit_cost: 0 }] });
  const addFromCatalog = (id) => {
    const c = catalog.find((x) => x.id === id);
    if (!c) return;
    setForm((f) => ({ ...f, items: [...f.items.filter((it) => it.description || it.unit_price), { description: c.name, quantity: 1, unit_price: c.unit_price, unit_cost: c.unit_cost }] }));
  };
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });

  const subtotal = form.items.reduce((a, b) => a + (Number(b.quantity) || 0) * (Number(b.unit_price) || 0), 0);
  const cost = form.items.reduce((a, b) => a + (Number(b.quantity) || 0) * (Number(b.unit_cost) || 0), 0);
  const tax = subtotal * (Number(form.tax_rate) || 0) / 100;
  const total = subtotal + tax;
  const profit = subtotal - cost;

  const payloadOf = (f) => ({
    customer_id: f.customer_id, customer_name: f.customer_name, customer_email: f.customer_email,
    items: f.items.map((it) => ({ description: it.description, quantity: Number(it.quantity) || 0, unit_price: Number(it.unit_price) || 0, unit_cost: Number(it.unit_cost) || 0 })),
    tax_rate: Number(f.tax_rate) || 0, status: f.status, valid_until: f.valid_until,
    deposit_required: !!f.deposit_required, deposit_amount: Number(f.deposit_amount) || 0, notes: f.notes,
  });

  const save = async () => {
    if (!form.customer_name.trim()) return toast.error("Select a customer");
    try {
      if (editing) await api.put(`/estimates/${editing}`, payloadOf(form));
      else await api.post("/estimates", payloadOf(form));
      toast.success(editing ? "Estimate updated" : "Estimate created"); setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => { await api.delete(`/estimates/${id}`); toast.success("Deleted"); load(); };
  const toInvoice = async (id) => { try { await api.post(`/estimates/${id}/convert-invoice`); toast.success("Converted to invoice"); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const toJob = async (id) => { try { await api.post(`/estimates/${id}/convert-job`); toast.success("Job scheduled from estimate"); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };

  return (
    <div className="space-y-6" data-testid="estimates-page">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">Branded estimates that convert — with built-in job costing.</p>
        <Button data-testid="add-estimate-btn" onClick={openNew} className="bg-primary text-primary-foreground hover:bg-[#152e17] hover:text-white transition-colors duration-200"><Plus className="h-4 w-4 mr-1.5" /> New Estimate</Button>
      </div>

      <Card className="border-border rounded-lg overflow-hidden bg-card">
        <Table>
          <TableHeader><TableRow className="bg-muted/50">
            <TableHead>Estimate #</TableHead><TableHead>Customer</TableHead><TableHead className="text-right">Total</TableHead>
            <TableHead className="text-right">Profit</TableHead><TableHead>Status</TableHead><TableHead>Valid Until</TableHead><TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-muted-foreground">No estimates yet.</TableCell></TableRow>
            ) : items.map((e) => (
              <TableRow key={e.id} data-testid="estimate-row" className="hover:bg-muted/30 transition-colors duration-200">
                <TableCell className="font-semibold">{e.estimate_number}</TableCell>
                <TableCell>{e.customer_name}</TableCell>
                <TableCell className="text-right tabular-nums font-semibold">{money(e.total)}</TableCell>
                <TableCell className="text-right tabular-nums text-primary">{money(e.profit)} <span className="text-xs text-muted-foreground">({e.margin}%)</span></TableCell>
                <TableCell><Badge className={`capitalize border-0 ${statusStyle[e.status]}`}>{e.status}</Badge>{e.converted_invoice_id && <Badge variant="outline" className="ml-1 text-[0.6rem] border-primary/30 text-primary">Invoiced</Badge>}</TableCell>
                <TableCell className="text-muted-foreground text-sm">{e.valid_until}</TableCell>
                <TableCell><div className="flex justify-end gap-1">
                  {!e.converted_invoice_id && <button data-testid="convert-invoice-btn" title="Convert to invoice" onClick={() => toInvoice(e.id)} className="p-2 rounded-md hover:bg-primary/10 transition-colors duration-200"><FileCheck className="h-4 w-4 text-primary" /></button>}
                  {!e.converted_job_id && <button data-testid="convert-job-btn" title="Schedule as job" onClick={() => toJob(e.id)} className="p-2 rounded-md hover:bg-accent/10 transition-colors duration-200"><CalendarPlus className="h-4 w-4 text-accent" /></button>}
                  <button data-testid="edit-estimate-btn" onClick={() => openEdit(e)} className="p-2 rounded-md hover:bg-muted transition-colors duration-200"><Pencil className="h-4 w-4 text-muted-foreground" /></button>
                  <button data-testid="delete-estimate-btn" onClick={() => remove(e.id)} className="p-2 rounded-md hover:bg-destructive/10 transition-colors duration-200"><Trash2 className="h-4 w-4 text-destructive" /></button>
                </div></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? `Edit ${form.estimate_number || "Estimate"}` : "New Estimate"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Customer *</Label>
                <Select value={form.customer_id || "none"} onValueChange={(v) => onCustomer(v === "none" ? "" : v)}><SelectTrigger data-testid="estimate-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger><SelectContent><SelectItem value="none">— None —</SelectItem>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="space-y-1.5"><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}><SelectTrigger data-testid="estimate-status-select"><SelectValue /></SelectTrigger><SelectContent>{["draft", "sent", "accepted", "declined"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="space-y-1.5"><Label>Valid Until</Label><Input type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Add from catalog</Label>
                <Select value="" onValueChange={addFromCatalog}><SelectTrigger data-testid="estimate-catalog-select"><SelectValue placeholder="Quick-add item" /></SelectTrigger><SelectContent>{catalog.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} — {money(c.unit_price)}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2"><Label>Line Items</Label><Button type="button" variant="ghost" size="sm" data-testid="add-estimate-item-btn" onClick={addItem}><Plus className="h-3.5 w-3.5 mr-1" /> Add</Button></div>
              <div className="grid grid-cols-12 gap-2 text-[0.65rem] uppercase tracking-wide text-muted-foreground px-1 mb-1"><span className="col-span-5">Description</span><span className="col-span-2">Qty</span><span className="col-span-2">Price</span><span className="col-span-2">Cost</span></div>
              <div className="space-y-2">
                {form.items.map((it, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <Input className="col-span-5" data-testid="estimate-item-desc" placeholder="Description" value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} />
                    <Input className="col-span-2" type="number" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} />
                    <Input className="col-span-2" type="number" value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} />
                    <Input className="col-span-2" type="number" value={it.unit_cost} onChange={(e) => setItem(i, "unit_cost", e.target.value)} />
                    <button type="button" onClick={() => removeItem(i)} className="col-span-1 p-2 rounded hover:bg-destructive/10 transition-colors duration-200"><X className="h-4 w-4 text-muted-foreground" /></button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-between items-start">
              <div className="space-y-3">
                <div className="space-y-1.5 w-32"><Label>Tax Rate (%)</Label><Input type="number" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} /></div>
                <div className="flex items-center gap-2"><Switch checked={form.deposit_required} onCheckedChange={(v) => setForm({ ...form, deposit_required: v })} /><Label className="mb-0 text-sm">Require deposit</Label></div>
                {form.deposit_required && <div className="space-y-1.5 w-32"><Label>Deposit ($)</Label><Input type="number" value={form.deposit_amount} onChange={(e) => setForm({ ...form, deposit_amount: e.target.value })} /></div>}
              </div>
              <div className="w-full sm:w-60 space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="tabular-nums">{money(subtotal)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Tax</span><span className="tabular-nums">{money(tax)}</span></div>
                <div className="flex justify-between text-muted-foreground"><span>Est. cost</span><span className="tabular-nums">{money(cost)}</span></div>
                <div className="flex justify-between text-primary"><span>Profit</span><span className="tabular-nums">{money(profit)}</span></div>
                <div className="flex justify-between font-display font-bold text-base pt-1.5 border-t border-border"><span>Total</span><span data-testid="estimate-total" className="tabular-nums">{money(total)}</span></div>
              </div>
            </div>

            <div className="space-y-1.5"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="save-estimate-btn" onClick={save} className="bg-primary text-primary-foreground hover:bg-[#152e17] hover:text-white transition-colors duration-200">Save Estimate</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
